import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsContainer, { SettingsSettingType } from '../../../components/settings/SettingsContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import SmsCreditOrderModal from './SmsCreditOrderModal';
import SmsTemplateList from './SmsTemplateList';
import '../../../style/scss/sms.scss';

// The shape of the sms credit overview response
interface SmsCreditOverview {
    balance: number;
    scheduled_count: number;
    bundles: SmsCreditBundle[];
}

export interface SmsCreditBundle {
    key: string;
    quantity: number;
    unit_price: string;
    subtotal: string;
    transaction_fee: string;
    total_ex_vat: string;
    vat_amount: string;
    total_inc_vat: string;
}

const SmsSettings: React.FC = () => {
    const { t } = useTranslation();
    const { handleLogout, environmentType } = useAuthContext();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const [overview, setOverview] = useState<SmsCreditOverview | null>(null);

    // Fetch the credit overview of the environment
    const fetchOverview = async () => {
        const response = await fetchData({ apiUrl: 'get_sms_credit_overview', handleLogout });
        setOverview(response);
    };
    useEffect(() => {
        fetchOverview();
    }, []);

    // Open the credit order modal
    const openOrderModal = () => {

        // Trials must order their subscription before ordering sms credits
        if (environmentType === 'trial') {
            setFloatingAlert({ type: 'warning', message: t('sms.credits.order_requires_subscription_message') });
            return;
        }

        if (!overview) return;
        initializeModal(
            <SmsCreditOrderModal bundles={overview.bundles} onOrderCompleted={fetchOverview} />,
            { modalSize: 'medium' }
        );
    };

    // Determine whether the scheduled messages exceed the balance
    const insufficientForScheduled = overview !== null && overview.scheduled_count > overview.balance;

    // Configure the sms settings in the standard settings container
    const settings: SettingsSettingType[] = [
        {
            title: 'sms.settings.credits_title',
            scope: 'division',
            fields: [
                {
                    type: 'render',
                    render: () => (
                        <div className='sms-credit-card'>
                            <div className='sms-credit-numbers'>
                                <div className='sms-credit-number'>
                                    <span className='sms-credit-value'>{overview ? overview.balance : '...'}</span>
                                    <span className='sms-credit-label'>{t('sms.settings.credits_left_label')}</span>
                                </div>
                                <div className='sms-credit-number'>
                                    <span className='sms-credit-value'>{overview ? overview.scheduled_count : '...'}</span>
                                    <span className='sms-credit-label'>{t('sms.settings.credits_scheduled_label')}</span>
                                </div>
                                <div className='sms-credit-order-button'>
                                    <span className='sms-order-link' onClick={openOrderModal}>
                                        {t('sms.settings.order_more_button')}
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </span>
                                </div>
                            </div>
                            {insufficientForScheduled && (
                                <p className='error-text'>{t('sms.settings.credits_insufficient_warning')}</p>
                            )}
                        </div>
                    )
                }
            ]
        },
        {
            title: 'sms.settings.sender_name_title',
            explanationText: 'sms.settings.sender_name_helper_text',
            scope: 'division',
            fields: [
                {
                    name: 'sms_sender_name',
                    type: 'text',
                    placeholder: 'sms.settings.sender_name_placeholder',
                    maxLength: 11,
                },
            ]
        },
        {
            title: 'sms.settings.quiet_hours_title',
            scope: 'division',
            fields: [
                {
                    name: 'sms_quiet_hours_end',
                    type: 'time',
                    group: 'field-row',
                },
                {
                    name: 'sms_quiet_hours_start',
                    type: 'time',
                    group: 'field-row',
                },
                {
                    type: 'render',
                    render: () => (
                        <p className='explanation-text'>{t('sms.settings.quiet_hours_helper_text')}</p>
                    )
                },
            ]
        },
    ];

    return (
        <div className='sms-settings'>
            <SettingsContainer settings={settings} />
            <SmsTemplateList />
        </div>
    );
};

export default SmsSettings;
